/**
 * Shared contracts between the API, the web app and the desktop app.
 * Keep this file free of runtime dependencies so every client can import it.
 */

export const ROLES = ['ADMIN', 'MANAGER', 'USER'] as const;
export type RoleName = (typeof ROLES)[number];

export type UserStatus = 'ACTIVE' | 'INACTIVE';
export type ProjectStatus = 'ACTIVE' | 'ARCHIVED';
export type TimeEntrySource = 'WEB' | 'DESKTOP';

/**
 * Personal details. Present only when the caller is an admin or the person
 * themselves — managers never receive this, so treat it as optional.
 */
export interface UserPersonalDetails {
  /** YYYY-MM-DD. */
  dateOfBirth: string | null;
  /** YYYY-MM-DD. */
  anniversaryDate: string | null;
  phone: string | null;
  address: string | null;
}

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  username: string;
  status: UserStatus;
  mustChangePassword: boolean;
  roles: RoleName[];
  createdAt: string;
  /** Omitted entirely for callers who may not see it. */
  personal?: UserPersonalDetails;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  /** Seconds until the access token expires. */
  expiresIn: number;
}

export interface LoginResponse extends AuthTokens {
  user: AuthUser;
}

export interface Project {
  id: string;
  name: string;
  description: string | null;
  client: string | null;
  color: string;
  hourlyRate: number | null;
  status: ProjectStatus;
  createdAt: string;
  memberCount?: number;
  members?: ProjectMember[];
}

export interface ProjectMember {
  userId: string;
  name: string;
  username: string;
  email: string;
  /** A member flagged as manager can report on this project when they hold the MANAGER role. */
  isManager: boolean;
}

export interface TimeEntry {
  id: string;
  userId: string;
  userName?: string;
  projectId: string;
  projectName?: string;
  projectColor?: string;
  description: string | null;
  startTime: string;
  endTime: string | null;
  /** Seconds. Null while the entry is still running. */
  durationSeconds: number | null;
  source: TimeEntrySource;
  isManual: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Screenshot {
  id: string;
  userId: string;
  userName?: string;
  projectId: string | null;
  projectName?: string;
  timeEntryId: string | null;
  capturedAt: string;
  /** 0-100 blended keyboard + mouse activity for the capture interval. */
  activityLevel: number;
  keyboardCount: number;
  mouseCount: number;
  width: number | null;
  height: number | null;
  displayLabel: string | null;
  url?: string;
  thumbnailUrl?: string;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DashboardOverview {
  activeUsers: { userId: string; name: string; projectName: string; startedAt: string; source: TimeEntrySource }[];
  totals: { todaySeconds: number; weekSeconds: number; rangeSeconds: number };
  byProject: { projectId: string; projectName: string; color: string; seconds: number }[];
  byUser: { userId: string; userName: string; seconds: number }[];
  screenshotsToday: number;
}

export interface ReportRow {
  userId: string;
  userName: string;
  projectId: string;
  projectName: string;
  seconds: number;
  amount: number | null;
  entryCount: number;
}

export interface ReportResponse {
  rows: ReportRow[];
  totalSeconds: number;
  totalAmount: number | null;
  from: string;
  to: string;
}

/** One row of the deficit ladder: at or above `fromHours`, charge `days` leave. */
export interface DeficitLeaveBand {
  fromHours: number;
  days: number;
}

export interface AppSettings {
  /** Minutes between desktop screenshot captures. */
  screenshotIntervalMinutes: number;
  /** Days to keep screenshots before the purge worker deletes them. 0 = keep forever. */
  screenshotRetentionDays: number;
  /** Minutes of no input before the desktop app treats the user as idle. */
  idleTimeoutMinutes: number;
  /** When true the desktop app stops the timer automatically once idle. */
  stopTimerOnIdle: boolean;
  /** When false, employees may not create or edit time entries by hand. */
  allowManualTimeEntry: boolean;
  /**
   * Whether the desktop app shows a notification each time it captures.
   * Off silences it for everyone; on leaves the choice to each install, which
   * defaults to showing it. The first-run monitoring notice is separate and
   * always shown.
   */
  notifyOnCapture: boolean;
  /** Text shown in the desktop monitoring-consent dialog. */
  monitoringNoticeVersion: string;
  /**
   * Project that break time is logged against. When set, clients show a break
   * (coffee) button; every active user may log time to it without being a
   * member, and the desktop app pauses screenshot capture while it runs.
   */
  breakProjectId: string | null;

  // ---- attendance ---------------------------------------------------------
  /** IANA timezone attendance days are computed in, e.g. "Asia/Kolkata". */
  attendanceTimezone: string;
  /**
   * YYYY-MM-DD the organisation started tracking time here. Working days before
   * it are excluded exactly like days before someone joined — no absence, no
   * deficit, no deduction — because there is no data for them, not because
   * nobody worked.
   *
   * Leave accrual is deliberately NOT affected: the bank still fills from the
   * start of the leave year, and an opening balance is corrected with a grant.
   *
   * Null disables it.
   */
  attendanceStartsOn: string | null;
  /** 0 = Sunday … 6 = Saturday. Days off every week. */
  weekendDays: number[];
  /** Expected tracked work per workday, excluding breaks. */
  workdayHours: number;
  /** Expected break per workday; shown alongside, not counted as work. */
  breakHours: number;
  /** At or above this much work a day counts as a half day rather than leave. */
  halfDayHours: number;
  /**
   * Upper bound of the half-day range. Above this a day is a *full* day even if
   * it falls short of workdayHours; the gap becomes deficit instead of costing
   * half a day. Defaults to workdayHours, which reproduces the old behaviour.
   */
  halfDayMaxHours: number;
  /** "HH:MM" local time the workday starts; first clock-in after start + grace is late. */
  workdayStart: string;
  /**
   * "HH:MM" local time every running timer is stopped at, and after which no
   * timer can be started until midnight. Null = timers run until stopped.
   */
  timerCutoffTime: string | null;
  lateGraceMinutes: number;

  // ---- salary -------------------------------------------------------------
  /** ISO 4217 code used for display, e.g. "INR". */
  currency: string;
  /** Attendance bonus by leave units in the month. */
  attendanceBonusNoLeave: number;
  attendanceBonusOneLeave: number;
  attendanceBonusTwoPlusLeaves: number;
  /** Leave days that carry full pay each month (no carry-over). */
  paidLeavesPerMonth: number;
  /** How much of a leave a half day counts as, for deductions and the bonus tier. */
  halfDayLeaveWeight: number;
  /**
   * Unused paid leave carries forward month to month until this month (1-12),
   * whose sheet pays the remaining balance out at the per-day rate. The bank
   * then restarts from zero.
   */
  leaveYearEndMonth: number;
  /** Turn the year-end payout off to simply forfeit the balance. */
  encashLeavesAtYearEnd: boolean;
  /**
   * Converts a month's net hours deficit (short hours minus surplus) into leave
   * days. The highest band whose `fromHours` is not above the deficit wins;
   * below the first band nothing is charged, so row one is the free allowance.
   * Empty disables the feature entirely.
   *
   * Deducted from pay and counted toward the attendance bonus, but never drawn
   * from the paid leave bank.
   */
  deficitLeaveBands: DeficitLeaveBand[];

  // ---- leave applications ---------------------------------------------------
  /**
   * Working days of notice casual leave needs, counted back from the first
   * leave day. 0 = by the previous working day; 1 = Thursday for a Monday.
   */
  casualLeaveNoticeWorkingDays: number;
  /** When false, medical leave waits for an admin like casual leave does. */
  autoApproveMedicalLeave: boolean;
}

export interface LeaveBankEntry {
  id: string;
  userId: string;
  month: string;
  days: number;
  reason: string;
  createdByName: string | null;
  createdAt: string;
}

export interface LeaveAdjustment {
  id: string;
  userId: string;
  month: string;
  days: number;
  reason: string;
  createdByName: string | null;
  createdAt: string;
}

export interface LeaveBankMonth {
  month: string;
  opening: number;
  accrued: number;
  granted: number;
  grants: LeaveBankEntry[];
  /** Leave units taken that month (leaves + half days × weight + adjustments). */
  leaveUnits: number;
  /** Units covered by an approved application (or added by an admin adjustment). */
  approvedUnits: number;
  /** Absences with no approved application — never paid from the bank. */
  unapprovedUnits: number;
  used: number;
  /** Units beyond the bank, plus every unapproved unit — unpaid. */
  unpaid: number;
  closing: number;
}

/** What an employee sees about their own leave — days only, never money. */
export interface LeaveBankLedger {
  userId: string;
  userName: string;
  joinedOn: string | null;
  /** First month of the current leave year. */
  yearStart: string;
  yearEndMonth: number;
  encashAtYearEnd: boolean;
  paidLeavesPerMonth: number;
  /** As of the requested month. */
  month: string;
  available: number;
  months: LeaveBankMonth[];
  totals: { accrued: number; granted: number; used: number; unpaid: number };
}

export interface SalaryRow {
  userId: string;
  userName: string;
  month: string;
  /** null until an admin sets it; the row still shows attendance. */
  monthlySalary: number | null;
  /** monthlySalary × workingDays ÷ calendarWorkingDays — the full salary unless the employee joined mid-month. */
  grossPay: number | null;
  workingDays: number;
  elapsedWorkingDays: number;
  perDayRate: number | null;
  presentDays: number;
  halfDays: number;
  leaveDays: number;
  lateDays: number;
  noBreakDays: number;
  adjustmentDays: number;
  adjustments: LeaveAdjustment[];
  /** leaves + halfDays × weight + adjustments. */
  leaveUnits: number;
  /** Covered by an approved application, or added by an admin adjustment. */
  approvedLeaveUnits: number;
  /** Absent with no approved application — unpaid even if the bank has days. */
  unapprovedLeaveUnits: number;
  paidLeavesUsed: number;
  unpaidLeaveUnits: number;
  /** Net hours deficit for the month, after surplus offsets. */
  netDeficitHours: number;
  /** Leave days the deficit converted to. Included in leaveUnits; never paid from the bank. */
  deficitLeaveUnits: number;
  /** YYYY-MM-DD, or null when only createdAt is known. */
  joinedOn: string | null;
  /** Leave bank for the year, as of this month. */
  bank: {
    /** Balance carried in from the previous month (0 in the first month of the year or at joining). */
    opening: number;
    /** paidLeavesPerMonth for this month, 0 before joining. */
    accrued: number;
    /** Manual grants landing this month. */
    granted: number;
    grants: LeaveBankEntry[];
    /** Leave units drawn from the bank this month (= paidLeavesUsed). */
    used: number;
    closing: number;
    /** True when this is the year-end month and the closing balance is paid out. */
    encashed: boolean;
  };
  /** closing × perDayRate in the year-end month, else 0. */
  encashment: number | null;
  deduction: number | null;
  bonus: number;
  /** grossPay − deduction + bonus + encashment */
  netPayable: number | null;
  /** True while the month is not over: figures will still move. */
  projected: boolean;
}

export type CalendarExceptionKind = 'HOLIDAY' | 'WORKING';

export interface CalendarException {
  id: string;
  /** YYYY-MM-DD */
  date: string;
  kind: CalendarExceptionKind;
  name: string;
}

export type LeaveType = 'CASUAL' | 'MEDICAL';
export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface LeaveApplication {
  id: string;
  userId: string;
  userName?: string;
  type: LeaveType;
  status: LeaveStatus;
  /** YYYY-MM-DD, inclusive. */
  fromDate: string;
  toDate: string;
  halfDay: boolean;
  /** Working days covered (0.5 for a half day). */
  units: number;
  reason: string;
  /** Admin who filed this for the employee; null when they filed it themselves. */
  filedByName: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}

/** The editable HR handbook. Markdown; rendered read-only for everyone. */
export interface Handbook {
  content: string;
  updatedAt: string | null;
  updatedByName: string | null;
}

export type DayKind = 'workday' | 'weekend' | 'holiday' | 'working-weekend';
export type DayStatus = 'present' | 'half' | 'leave' | 'off' | 'upcoming' | 'in-progress';

export interface AttendanceDay {
  /** YYYY-MM-DD in the attendance timezone. */
  date: string;
  kind: DayKind;
  /** Name of the holiday or working-day override, if any. */
  label: string | null;
  status: DayStatus;
  late: boolean;
  workSeconds: number;
  breakSeconds: number;
  /** Seconds short of the expected day. Only full days accrue this; absences do not. */
  deficitSeconds: number;
  /** Seconds beyond expectation on this day. */
  surplusSeconds: number;
  /** ISO timestamps of first clock-in and last clock-out that day. */
  firstIn: string | null;
  lastOut: string | null;
  entryCount: number;
  /** A leave application covering this day, if any (latest non-cancelled). */
  leave: { id: string; type: LeaveType; status: LeaveStatus; halfDay: boolean } | null;
}

export interface AttendanceTotals {
  /** Working days the employee was employed for this month. */
  workingDays: number;
  /** Working days in the calendar month, ignoring the joining date — the pay basis. */
  calendarWorkingDays: number;
  /** Working days up to and including today (what the counts are out of). */
  elapsedWorkingDays: number;
  presentDays: number;
  halfDays: number;
  leaveDays: number;
  lateDays: number;
  workSeconds: number;
  breakSeconds: number;
  /** workdayHours × elapsed working days. */
  expectedSeconds: number;
  /**
   * Seconds short of workdayHours, counted on full days only. Absent days are
   * excluded: they are already charged as a leave day, and counting their
   * missing hours here would charge the same absence twice.
   */
  deficitSeconds: number;
  /** Seconds beyond expectation — past workdayHours on a full day, past halfDayHours on a half day. */
  surplusSeconds: number;
  /** breakHours × days actually worked (present + half). */
  expectedBreakSeconds: number;
  /**
   * Worked days (present or half) with no break logged at all — the tell-tale
   * of someone logging the whole day against a project.
   */
  noBreakDays: number;
}

export interface AttendanceMonth {
  userId: string;
  userName: string;
  month: string;
  timezone: string;
  days: AttendanceDay[];
  totals: AttendanceTotals;
}

export interface AttendanceSummaryRow {
  userId: string;
  userName: string;
  totals: AttendanceTotals;
}

/** The break project as clients need it — resolved server-side from settings. */
export interface BreakProject {
  id: string;
  name: string;
  color: string;
}

export interface AuditLogEntry {
  id: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  metadata: Record<string, unknown> | null;
  ip: string | null;
  createdAt: string;
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

/** Formats a duration in seconds as `H:MM:SS`. */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/** Formats a duration in seconds as `Hh MMm`, the form used in reports. */
/**
 * Hours the month is short, after hours worked beyond expectation offset it.
 *
 * Not `expectedSeconds - workSeconds`: that counts an absent day's whole eight
 * hours as a shortfall even though the day is already charged as a leave, so
 * someone with two approved leaves and otherwise perfect attendance appears
 * sixteen hours short. Only full days accrue deficit (see services/attendance).
 */
export function netDeficitSeconds(t: Pick<AttendanceTotals, 'deficitSeconds' | 'surplusSeconds'>): number {
  return Math.max(0, (t.deficitSeconds ?? 0) - (t.surplusSeconds ?? 0));
}

export function formatHours(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

export function hasRole(user: Pick<AuthUser, 'roles'> | null | undefined, ...roles: RoleName[]): boolean {
  if (!user) return false;
  return roles.some((r) => user.roles.includes(r));
}
